import React from "react";
import useExcelUpload from "../../../../utils/hooks/useExcelUpload";
import { parseExcelDate } from "../../../../utils/data/excelData";
import { formatDateForAPI } from "../../../../utils/hooks/dataHandler";
import UploadExcelDialog from "../../utils/UploadExcelEnhanced";
import {
  arrangementOptions,
  createClaimLine,
  createTrackerTransaction,
  normalizeTrackerShares,
  transactionTypeOptions,
} from "./sharedBasedPaymentData";
import {
  QUALIFYING_COLUMN_FIELDS,
  sanitizeNonNegativeAmount,
  sanitizeQualifyingColumnValue,
} from "./sharedBasedPaymentCalculation";

export const TRACKER_SHEET_NAME = "Cost Tracker";
export const QUALIFYING_SHEET_NAME = "Qualifying Deduction";

const DATA_SHEET_NAMES = [TRACKER_SHEET_NAME, QUALIFYING_SHEET_NAME];

const trackerHeaders = [
  "Date",
  "Type",
  "Shares",
  "Share cost",
  "Charges",
  "Linked line",
  "Reference",
  "Outflow description",
];

const qualifyingHeaders = [
  "Line ID",
  "Arrangement",
  "Description",
  "Exercise / Vesting Date",
  "Recharge / Liability Date",
  "Column I",
  "Column II",
  "Column III",
  "Column IV",
  "Employee Payment",
];

const instructions = [
  ["Share-Based Payment Excel Upload"],
  ["Use the Tracker and Qualifying Deduction worksheets in this workbook."],
  ["Do not rename either worksheet or change the column order."],
  [
    "A non-empty Tracker worksheet replaces transactions only; share pool settings are retained. Qualifying Deduction updates matching Line IDs and adds new lines; other existing lines are retained.",
  ],
  [
    "Any data in the Tracker worksheet automatically turns on Acquired Shares Cost Tracker.",
  ],
  [
    "Use a Line ID such as L001 to update an existing deduction line. If a Line ID appears more than once in this file, its last row wins. Use the same ID in Tracker > Linked line for an EEBR transfer.",
  ],
  [
    "Enter one transaction per row in Tracker. All imported transactions belong to the share pool already configured in this schedule.",
  ],
  ["For Other outflow, optionally enter a short explanation in Outflow description."],
  ["Dates may be entered as Excel dates or DD-MM-YYYY / YYYY-MM-DD."],
  [
    "Enter Shares as positive for Acquisition and negative for EEBR transfer or Other outflow. Imported share signs follow the selected Type.",
  ],
  [
    "Columns I-IV and Employee Payment must be whole numbers.",
  ],
  [
    "Share costs and qualifying amounts must be zero or positive. Invalid and inapplicable qualifying amounts are imported as blank cells.",
  ],
  ["After importing, review the calculated results and click Save."],
];

const hasValue = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

const hasRowData = (row) => Array.isArray(row) && row.some(hasValue);

const asText = (value) => (hasValue(value) ? String(value).trim() : "");

const asNumber = (value, decimals) => {
  if (!hasValue(value)) return 0;
  const text = String(value).trim();
  const negative = text.startsWith("(") && text.endsWith(")");
  const parsed = Number(text.replace(/[,$()\s]/g, ""));
  if (!Number.isFinite(parsed)) return 0;
  const number = Math.max(0, negative ? -parsed : parsed);
  if (decimals === undefined) return number;
  const factor = 10 ** decimals;
  return Math.round(number * factor) / factor;
};

const asWholeNumber = (value) => {
  if (!hasValue(value)) return 0;
  const text = String(value).trim();
  const negative = text.startsWith("(") && text.endsWith(")");
  const parsed = Number(text.replace(/[,$()\s]/g, ""));
  if (!Number.isFinite(parsed)) return 0;
  return Math.round(negative ? -Math.abs(parsed) : parsed);
};
const asCost = (value) => asNumber(value, 2);

const asDate = (value) => {
  if (!hasValue(value)) return "";
  const parsed = parseExcelDate(value);
  return parsed ? formatDateForAPI(parsed) : "";
};

const normalizeOption = (value, options, fallback) => {
  const input = asText(value).toLowerCase();
  if (!input) return fallback;
  const option = options.find(
    (item) =>
      item.value.toLowerCase() === input || item.label.toLowerCase() === input
  );
  return option?.value || fallback;
};

const buildQualifyingLines = (rows, currentData, useCostTracker) => {
  const claimLines = [...(currentData.claimLines || [])];
  const existingLineCount = claimLines.length;
  let invalidCellCount = 0;
  const lineIndexById = new Map(
    claimLines.map((line, index) => [asText(line.lineId).toLowerCase(), index])
  );
  const latestRows = new Map();
  rows.filter(hasRowData).forEach((row) => {
    const lineId = asText(row[0]).toLowerCase();
    // Blank IDs are independent new lines; only explicit IDs are upsert keys.
    const key = lineId || Symbol();
    latestRows.delete(key);
    latestRows.set(key, row);
  });
  const reservedLineIds = new Set([
    ...lineIndexById.keys(),
    ...[...latestRows.values()]
      .map((row) => asText(row[0]).toLowerCase())
      .filter(Boolean),
  ]);
  let nextLineNumber = Math.max(
    0,
    ...[...reservedLineIds].map((id) => {
      const match = /^l(\d+)$/.exec(id);
      return match ? Number(match[1]) : 0;
    })
  ) + 1;
  const nextImportLineId = () => {
    let lineId;
    do {
      lineId = `L${String(nextLineNumber++).padStart(3, "0")}`;
    } while (reservedLineIds.has(lineId.toLowerCase()));
    reservedLineIds.add(lineId.toLowerCase());
    return lineId;
  };
  let updatedLines = 0;
  let addedLines = 0;

  latestRows.forEach((row) => {
    const requestedLineId = asText(row[0]);
    const existingIndex = requestedLineId
      ? lineIndexById.get(requestedLineId.toLowerCase())
      : undefined;
    const existingLine =
      existingIndex === undefined ? undefined : claimLines[existingIndex];
    const arrangementInput = asText(row[1]).toLowerCase();
    const arrangementOption = arrangementOptions.find(
      (option) =>
        option.value.toLowerCase() === arrangementInput ||
        option.label.toLowerCase() === arrangementInput
    );
    const arrangement = arrangementOption?.value || "companyTreasury";
    const amounts = Object.fromEntries(
      QUALIFYING_COLUMN_FIELDS.map((field, index) => [
        field,
        arrangementOption
          ? sanitizeQualifyingColumnValue(field, row[index + 5], arrangement)
          : "",
      ])
    );
    const exerciseOrVestingDate = asDate(row[3]);
    const rechargeOrLiabilityDate =
      arrangement === "companyTreasury" ? "" : asDate(row[4]);
    const employeePayment = sanitizeNonNegativeAmount(row[9], 0);

    invalidCellCount += QUALIFYING_COLUMN_FIELDS.reduce(
      (count, field, index) =>
        count + (hasValue(row[index + 5]) && amounts[field] === "" ? 1 : 0),
      0
    );
    if (hasValue(row[3]) && !exerciseOrVestingDate) invalidCellCount += 1;
    if (hasValue(row[4]) && !rechargeOrLiabilityDate) invalidCellCount += 1;
    if (hasValue(row[9]) && employeePayment === "") invalidCellCount += 1;

    const importedValues = {
      arrangement,
      column:
        arrangementOptions.find((option) => option.value === arrangement)
          ?.column || "acquired",
      description: asText(row[2]),
      exerciseOrVestingDate,
      rechargeOrLiabilityDate,
      ...amounts,
      employeePayment,
      manualCostEntry: !useCostTracker,
    };
    if (existingLine) {
      claimLines[existingIndex] = { ...existingLine, ...importedValues };
      if (existingIndex < existingLineCount) updatedLines += 1;
    } else {
      const line = createClaimLine(
        { ...currentData, useCostTracker, claimLines },
        { ...importedValues, lineId: requestedLineId || nextImportLineId() }
      );
      lineIndexById.set(line.lineId.toLowerCase(), claimLines.length);
      claimLines.push(line);
      addedLines += 1;
    }
  });

  return { claimLines, invalidCellCount, updatedLines, addedLines };
};

const buildTrackerData = (rows, claimLines, currentData) => {
  const poolId = currentData.costPools?.[0]?.poolId || "pool-001";
  const lineIdMap = new Map();
  claimLines.forEach((line) => {
    const lineId = asText(line.lineId);
    if (lineId) lineIdMap.set(lineId.toLowerCase(), line.claimLineId);
  });

  const transactions = [];
  let invalidCellCount = 0;
  rows.filter(hasRowData).forEach((row, rowIndex) => {
    const type = normalizeOption(row[1], transactionTypeOptions, "acquisition");
    const linkedLineReference = asText(row[5]);
    const date = asDate(row[0]);
    const linkedClaimLineId =
      type === "eebrTransfer"
        ? lineIdMap.get(linkedLineReference.toLowerCase()) || ""
        : "";
    if (hasValue(row[0]) && !date) invalidCellCount += 1;
    if (type === "eebrTransfer" && linkedLineReference && !linkedClaimLineId) {
      invalidCellCount += 1;
    }
    const transaction = createTrackerTransaction(
      { ...currentData, transactions },
      type,
      poolId
    );

    transactions.push({
      ...transaction,
      date,
      sequence: rowIndex + 1,
      type,
      shares: normalizeTrackerShares(asWholeNumber(row[2]), type),
      shareCost:
        type === "acquisition" || type === "opening" ? asCost(row[3]) : "",
      directAcquisitionCharges:
        type === "acquisition" || type === "opening" ? asCost(row[4]) : "",
      linkedClaimLineId,
      reference: asText(row[6]),
      outflowDescription: type === "otherOutflow" ? asText(row[7]) : "",
    });
  });

  return { transactions, invalidCellCount };
};

export const buildSharedBasedPaymentExcelImport = (
  currentData,
  excelData = {}
) => {
  const trackerRows = Array.isArray(excelData[TRACKER_SHEET_NAME])
    ? excelData[TRACKER_SHEET_NAME].filter(hasRowData)
    : [];
  const qualifyingRows = Array.isArray(excelData[QUALIFYING_SHEET_NAME])
    ? excelData[QUALIFYING_SHEET_NAME].filter(hasRowData)
    : [];
  const hasTrackerData = trackerRows.length > 0;
  const hasQualifyingData = qualifyingRows.length > 0;
  const useCostTracker = hasTrackerData
    ? true
    : Boolean(currentData.useCostTracker);
  const qualifyingImport = hasQualifyingData
    ? buildQualifyingLines(qualifyingRows, currentData, useCostTracker)
    : {
        claimLines: currentData.claimLines || [],
        invalidCellCount: 0,
      };
  const claimLines = qualifyingImport.claimLines;
  const validClaimLineIds = new Set(claimLines.map((line) => line.claimLineId));

  const trackerData = hasTrackerData
    ? buildTrackerData(trackerRows, claimLines, currentData)
    : {
        transactions: (currentData.transactions || []).filter(
          (transaction) =>
            !transaction.linkedClaimLineId ||
            validClaimLineIds.has(transaction.linkedClaimLineId)
        ),
      };

  return {
    data: {
      ...currentData,
      useCostTracker,
      claimLines: claimLines.map((line) => ({
        ...line,
        manualCostEntry: !useCostTracker,
      })),
      transactions: trackerData.transactions,
    },
    summary: {
      trackerRows: hasTrackerData ? trackerData.transactions.length : 0,
      qualifyingRows: hasQualifyingData
        ? qualifyingImport.updatedLines + qualifyingImport.addedLines
        : 0,
      qualifyingUpdated: qualifyingImport.updatedLines || 0,
      qualifyingAdded: qualifyingImport.addedLines || 0,
      trackerImported: hasTrackerData,
      qualifyingImported: hasQualifyingData,
      trackerEnabledAutomatically:
        hasTrackerData && !currentData.useCostTracker,
      invalidCellsLeftBlank:
        qualifyingImport.invalidCellCount +
        (hasTrackerData ? trackerData.invalidCellCount : 0),
    },
  };
};

const SharedBasedPaymentExcelUpload = ({ data, open, onClose, onApply }) => {
  const {
    selectedFile,
    uploading,
    alertMessage,
    alertSeverity,
    errorFileUrl,
    rowCount,
    handleCloseAlert,
    handleCloseDialog,
    handleDownloadMultipleTemplate,
    handleFileMultiSheetChange,
    handleResetFile,
    handleUploadMultiSheet,
  } = useExcelUpload({
    fileName: "share_based_payment_upload_template.xlsx",
    sheets: [
      {
        name: "Instructions",
        data: instructions,
        columnWidths: [110],
        type: "aoa",
      },
      {
        name: TRACKER_SHEET_NAME,
        data: [trackerHeaders],
        columnWidths: trackerHeaders.map((header) =>
          Math.max(14, Math.min(30, header.length + 2))
        ),
        type: "aoa",
      },
      {
        name: QUALIFYING_SHEET_NAME,
        data: [qualifyingHeaders],
        columnWidths: qualifyingHeaders.map((header) =>
          Math.max(14, Math.min(30, header.length + 2))
        ),
        type: "aoa",
      },
    ],
    validationSchema: [],
    onUpload: (excelData) => {
      onApply(buildSharedBasedPaymentExcelImport(data, excelData));
    },
  });

  const closeDialog = () => {
    handleCloseDialog();
    onClose();
  };

  const fixedValueGroups = [
    {
      label: "Arrangement",
      values: arrangementOptions.map((option) => option.label),
    },
    {
      label: "Transaction Type",
      values: transactionTypeOptions.map((option) => option.label),
    },
  ];

  return (
    <UploadExcelDialog
      title='Share-Based Payment'
      open={open}
      selectedFile={selectedFile}
      uploading={uploading}
      alertMessage={alertMessage}
      alertSeverity={alertSeverity}
      errorFileUrl={errorFileUrl}
      validRows={rowCount}
      onClose={closeDialog}
      onDownloadTemplate={handleDownloadMultipleTemplate}
      onFileChange={(event) =>
        handleFileMultiSheetChange(event, DATA_SHEET_NAMES)
      }
      onUpload={handleUploadMultiSheet}
      onCloseAlert={handleCloseAlert}
      onReset={handleResetFile}
      templateInstructions={instructions}
      fixedValueGroups={fixedValueGroups}
      infoTitle='Two sections, one workbook'
      infoMessage="Upload Tracker transactions and Qualifying Deduction lines together, or populate only one sheet. Importing transactions retains this schedule's share pool settings and automatically enables Use tracker."
    />
  );
};

export default SharedBasedPaymentExcelUpload;
